import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { SmartControlTestRecord } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { GridColumnFlexEnd } from 'src/components/ModalForms/ModalForm.styles'
import { ControlModelId } from 'src/pages/Controls/IndexItem/ControlsListItem.components'
import { GridColumn, GridRow } from '../../Grid'
import { Title } from '../../Text/Title'
import { ControlPolicyInfo } from '../../ListItem/ListItem.components'
import { CheckBoxInput } from '../../Inputs/CheckBox/CheckBoxInput'
import { DepartmentChip } from '../../../../pages/Controls/DepartmentChip'

export interface SelectControlsRowItemBaseProps {
  control: Control
  selectedControlIds: string[]
  setSelectedControlIds: Dispatch<SetStateAction<string[]>>
}

interface SelectControlsRowItemProps extends SelectControlsRowItemBaseProps {
  customPolicyElement?: JSX.Element
  customDepartmentElement?: JSX.Element
  modelIdAsId: boolean
  controlTestResult?: SmartControlTestRecord
  hideCheckbox?: boolean
  optionalColumn?: JSX.Element
  disabled?: boolean
}

const selectControlsRowItemDefaultProps = {
  modelIdAsId: false,
}

export const SelectControlsRowItem = ({
  control,
  selectedControlIds,
  setSelectedControlIds,
  customPolicyElement,
  customDepartmentElement,
  modelIdAsId,
  hideCheckbox,
  optionalColumn,
  disabled,
}: SelectControlsRowItemProps): JSX.Element => {
  const selectedControls = new Set(selectedControlIds)
  const controlId = modelIdAsId ? control.getModelId() : control.getId()
  const name = control.getName()
  const departmentId = control.getDepartmentId()
  const customId = control.getModelId()
  const isNotApplicable = control.getStatus() === CONTROL_STATUS.NOTAPPLICABLE

  const onChangeHandler = () => {
    selectedControls.has(controlId)
      ? selectedControls.delete(controlId)
      : selectedControls.add(controlId)
    setSelectedControlIds(Array.from(selectedControls))
  }

  const controlAsObject = useMemo(() => {
    return control.toObject()
  }, [control])

  return (
    <GridRow id={`select-controls-item-${controlId}`}>
      {!hideCheckbox && (
        <GridColumn>
          <CheckBoxInput
            id={controlId}
            checked={selectedControls.has(controlId)}
            onClick={(e) => e.stopPropagation()}
            onChange={onChangeHandler}
            requiredCapabilities={[CAPABILITY.CAN_LINK_REQUEST_CONTROLS]}
            disabled={disabled}
          />
        </GridColumn>
      )}
      <GridColumn>
        {customDepartmentElement || (
          <DepartmentChip departmentId={departmentId} />
        )}
      </GridColumn>
      <GridColumn>
        <ControlModelId modelId={customId} />
      </GridColumn>
      <GridColumn
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="center"
      >
        {customPolicyElement || (
          <ControlPolicyInfo
            control={controlAsObject}
            isControlNa={isNotApplicable}
          />
        )}
        <Title name={name} />
      </GridColumn>
      {optionalColumn && (
        <GridColumnFlexEnd>{optionalColumn}</GridColumnFlexEnd>
      )}
    </GridRow>
  )
}

SelectControlsRowItem.defaultProps = selectControlsRowItemDefaultProps
